import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { FaUserCircle } from 'react-icons/fa';
import Button from './atoms/Button';
import config from '../config/config';
import { RootState, logout, useLazyGetBusinessBySubdomainQuery } from '../store';
import { auth } from '../config/firebase-config';
import getSubdomain from '../utils/getSubdomain';
import useUpdateTitle from '../hooks/useUpdateTitle';
import MenuDropdown from './atoms/MenuDropdown';
import { BusinessDTO } from '../models/business';
import Image from './atoms/Image';
import ThemeToggle from './ThemeToggle';
import AvanttiIcon from './icons/AvanttiIcon';
import { UserType } from '../models/user';

const DesktopHeader: React.FC = () => {
  const navigate = useNavigate();
  const { token, user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const [business, setBusiness] = useState<BusinessDTO | null>(null);
  const [getBusinessBySubdomain, { isLoading: isLoadingBusiness }] = useLazyGetBusinessBySubdomainQuery();
  const subdomain = getSubdomain();
  const expectBusinessLogo = !!subdomain;

  useUpdateTitle(business?.name);

  useEffect(() => {
    if (subdomain) {
      getBusinessBySubdomain(subdomain).then((res) => {
        if (res.data) {
          setBusiness(res.data);
        }
      });
    }
  }, [subdomain]);

  const handleClick = (path: string) => {
    const baseUrl = config.baseDomain.replace(/^https?:\/\//, '');
    if (subdomain) {
      window.location.href = `https://${subdomain}.${baseUrl}${path}`;
    } else {
      navigate(path);
    }
  };

  const handleClickLogin = () => {
    window.location.href = `${config.baseDomain}/login`;
  };

  const handleLogout = (path: string) => {
    handleClick(path);
    signOut(auth)
      .then(() => {
        dispatch(logout());
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleClickSellAvantti = () => {
    window.open('https://organizadores.avantti.io/', '_blank');
  };

  const handleClickHelp = () => {
    window.open('https://wa.me/message/GV2TPBX7VGT5A1', '_blank');
  };

  return (
    <>
      <header className="fixed z-50 top-0 left-0 w-screen flex py-4 flex-col justify-center items-center self-center border-b border-border-secondary shadow-sm bg-blur-lg bg-opacity-heavy">
        <div className="flex px-8 items-center gap-8 self-stretch">
          <div className="flex items-center gap-10 flex-[1_0_0]">
            <div className="flex items-center">
              <span
                onClick={() => {
                  handleClick('/');
                }}
                className="flex h-18 cursor-pointer items-center"
              >
                {!expectBusinessLogo && <AvanttiIcon className="mr-3 text-text-primary" />}
                {expectBusinessLogo && business && <Image
                  isDownloading={isLoadingBusiness}
                  src={business.logo}
                  className="mr-3 dark:invert"
                  height={40}
                  width={120}
                />}
              </span>
            </div>

            <nav className="flex items-center gap-8">
              <a className="cursor-pointer flex py-0.5 flex-col align-center text-text-primary hover:text-primary-600 transition-colors" onClick={() => handleClick('/')}>
                Home
              </a>
              {/* <a className="cursor-pointer flex py-0.5 flex-col align-center">
                <div className="flex justify-center items-center gap-2">
                  <div>Categorias</div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                    <path d="M5.20001 7.5L10.2 12.5L15.2 7.5" stroke="#3F3F46" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </a> */}
              {((business && business?.is_white_label === false) || !business) && <a className="cursor-pointer flex py-0.5 flex-col align-center text-text-primary hover:text-primary-600 transition-colors" onClick={handleClickSellAvantti}>
                Quiero vender con avantti
              </a>}
              <a className="cursor-pointer flex py-0.5 flex-col align-center text-text-primary hover:text-primary-600 transition-colors" onClick={handleClickHelp}>
                Ayuda
              </a>
            </nav>
          </div>
          {/* <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="#222222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg> */}

          <div className='w-fit'>
            <ThemeToggle />
          </div>

          {!token && (
            <div className='w-fit'>
              <Button variant="secondary" onClick={handleClickLogin}>
                Iniciar sesión
              </Button></div>
          )}

          {token && (
            <div className='w-fit items-center bg-surface-base rounded-3xl shadow-sm border border-gradient-brand flex'>
              <MenuDropdown
                label={user.username}
                data={user}
                options={[
                  ...(user.type === UserType.ROOT ? [{
                    label: 'Panel de control',
                    onClick: () => handleClick('/admin'),
                  }] : []),
                  ...(user?.user_businesses?.length >= 0 || user?.type === 'ROOT' ? [{
                    label: 'Mis productoras',
                    onClick: () => handleClick('/admin/businesses'),
                  }] : []),
                  ...(user?.user_events?.length >= 1 ? [{
                    label: 'Mis eventos',
                    onClick: () => handleClick('/admin/all-events'),
                  }] : []),
                  {
                    label: 'Mis invitaciones',
                    onClick: () => handleClick('/admin/invitations'),
                  },
                  {
                    label: 'Cerrar sesión',
                    onClick: () => handleLogout('/'),
                    labelClassName: 'font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-md px-2 py-1 mx-2 mt-1 text-center transition-colors',
                  },
                ]}
                icon={<FaUserCircle className=" text-3xl h-[36px] ml-2" />}
                className="w-fit"
              />
            </div>
          )}
        </div>
      </header>
      <div className="h-[76px]" />
    </>
  );
};

export default DesktopHeader;
